/**
 * Frequency spectrum visualizer — combined EQ + seek bar.
 *
 * Draws EQ bars with two layers:
 *   - Dim layer: full-width bars at low opacity (unplayed)
 *   - Bright layer: same bars clipped to the played region (played)
 * A thin playhead line marks the current position.
 * A 2px baseline across the full width shows track progress even when paused.
 * Click or drag anywhere to seek.
 *
 * When there's no live audio signal (nothing has played yet, or playback is
 * paused), a static placeholder skyline is drawn instead of flat silence, so
 * the box still reads as an EQ rather than empty space.
 */
const Visualizer = (() => {

  const BAR_COUNT = 80;
  const FFT_SIZE  = 256; // 128 bins

  // Deterministic pseudo-random placeholder skyline (0–1 per bar index).
  function idleBarValue(i) {
    const x = Math.sin(i * 12.9898) * 43758.5453;
    return 0.15 + (x - Math.floor(x)) * 0.55;
  }

  let audioCtx     = null;
  let analyser     = null;
  let capturedNode = null;
  let dataArray    = null;

  // Howler (html5 mode) recycles a small pool of <audio> elements across
  // track changes, and createMediaElementSource() can only ever be called
  // once per element for the life of the page (a second call throws). Cache
  // the source node per element so a pooled element seen again just gets
  // reconnected instead of re-captured.
  const sourcesByNode = new WeakMap();

  let canvas      = null;
  let ctx         = null;
  let raf         = null;
  let seeking     = false;

  let getProgress = () => 0;
  let onSeek      = null;

  // Resolved from --tr-green so the progress fill/playhead follow the theme.
  let progressColor    = '#ffffff';
  let progressColorRGB = '255, 255, 255';

  function resolveProgressColor() {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--tr-green').trim();
    if (!value) return;
    progressColor = value;

    // Resolve to r,g,b via a throwaway element so it works whether --tr-green
    // is hex, rgb(), or a named color.
    const probe = document.createElement('span');
    probe.style.color = value;
    document.body.appendChild(probe);
    const rgb = getComputedStyle(probe).color.match(/\d+(\.\d+)?/g);
    document.body.removeChild(probe);
    if (rgb && rgb.length >= 3) progressColorRGB = `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`;
  }

  // ─── Init ────────────────────────────────────────────────────────────

  function init(canvasEl, callbacks = {}) {
    canvas      = canvasEl;
    ctx         = canvas.getContext('2d');
    getProgress = callbacks.getProgress || (() => 0);
    onSeek      = callbacks.onSeek      || null;

    canvas.style.cursor = 'pointer';

    resolveProgressColor();
    resize();
    window.addEventListener('resize', resize);
    bindSeek();
    draw();
  }

  function resize() {
    if (!canvas) return;
    const dpr    = window.devicePixelRatio || 1;
    canvas.width  = canvas.offsetWidth  * dpr;
    canvas.height = canvas.offsetHeight * dpr;
  }

  // ─── Seek interaction ────────────────────────────────────────────────

  function bindSeek() {
    function ratio(e) {
      const rect    = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    }

    canvas.addEventListener('mousedown', (e) => {
      seeking = true;
      if (onSeek) onSeek(ratio(e));
    });
    canvas.addEventListener('mousemove', (e) => {
      if (seeking && onSeek) onSeek(ratio(e));
    });
    document.addEventListener('mouseup', () => { seeking = false; });

    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      seeking = true;
      if (onSeek) onSeek(ratio(e));
    }, { passive: false });
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (seeking && onSeek) onSeek(ratio(e));
    }, { passive: false });
    document.addEventListener('touchend', () => { seeking = false; });
  }

  // ─── Audio connection ────────────────────────────────────────────────

  function ensureAudioCtx() {
    if (audioCtx) return true;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioCtx.createAnalyser();
      analyser.fftSize               = FFT_SIZE;
      analyser.smoothingTimeConstant = 0.78;
      analyser.connect(audioCtx.destination);
      dataArray = new Uint8Array(analyser.frequencyBinCount);
      return true;
    } catch (e) {
      console.warn('[Visualizer] Web Audio API unavailable:', e);
      return false;
    }
  }

  function connect(howl) {
    if (!howl || !ensureAudioCtx()) return;
    if (audioCtx.state === 'suspended') audioCtx.resume();

    const sound = howl._sounds[0];
    if (!sound?._node) return;
    const audioEl = sound._node;

    if (capturedNode === audioEl) return;

    // Disconnect whichever pooled element fed the analyser before, keyed by
    // its own cached source node — not a single shared variable — so a
    // stale reference can never leave two elements briefly wired into the
    // analyser/destination at once during a track transition.
    if (capturedNode) {
      const prevSource = sourcesByNode.get(capturedNode);
      if (prevSource) {
        try { prevSource.disconnect(); } catch (_) {}
      }
    }

    let nodeSource = sourcesByNode.get(audioEl);

    if (!nodeSource) {
      try {
        nodeSource = audioCtx.createMediaElementSource(audioEl);
        sourcesByNode.set(audioEl, nodeSource);
      } catch (e) {
        console.warn('[Visualizer] connect error:', e);
        return;
      }
    }

    try {
      nodeSource.connect(analyser);
      capturedNode = audioEl;
    } catch (e) {
      console.warn('[Visualizer] connect error:', e);
    }
  }

  // ─── Draw loop ───────────────────────────────────────────────────────

  function draw() {
    raf = requestAnimationFrame(draw);
    if (!ctx || !canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const W   = canvas.width;
    const H   = canvas.height;

    ctx.clearRect(0, 0, W, H);

    if (analyser) analyser.getByteFrequencyData(dataArray);

    const hasSignal = !!dataArray && dataArray.some(v => v > 2);

    const progress  = getProgress();
    const progressX = W * progress;
    const bins      = BAR_COUNT;
    const gap       = Math.max(dpr, Math.round(W * 0.003));
    const barW      = Math.max(dpr, (W - gap * (bins - 1)) / bins);

    // ── Baseline track line ──────────────────────────────────────────
    const lineH = Math.max(1, Math.round(dpr));

    ctx.fillStyle   = '#ffffff';
    ctx.globalAlpha = 0.15;
    ctx.fillRect(0, H - lineH, W, lineH);

    ctx.globalAlpha = 0.7;
    ctx.fillRect(0, H - lineH, progressX, lineH);

    // ── Dim bars — full width (unplayed appearance) ──────────────────
    ctx.globalAlpha = 0.2;
    ctx.fillStyle   = '#ffffff';

    for (let i = 0; i < bins; i++) {
      const val  = hasSignal ? dataArray[i] / 255 : idleBarValue(i);
      const barH = val * (H - lineH * 2);
      if (barH < 1) continue;
      ctx.fillRect(i * (barW + gap), H - lineH - barH, barW, barH);
    }

    // ── Bright bars — clipped to played region ───────────────────────
    if (progressX > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, progressX, H);
      ctx.clip();

      ctx.globalAlpha = 1;
      const grad = ctx.createLinearGradient(0, H, 0, 0);
      grad.addColorStop(0, 'rgba(255,255,255,0.55)');
      grad.addColorStop(1, 'rgba(255,255,255,1.0)');
      ctx.fillStyle = grad;

      for (let i = 0; i < bins; i++) {
        const val  = hasSignal ? dataArray[i] / 255 : idleBarValue(i);
        const barH = val * (H - lineH * 2);
        if (barH < 1) continue;
        ctx.fillRect(i * (barW + gap), H - lineH - barH, barW, barH);
      }

      ctx.restore();
    }

    // ── Progress wash — full-height translucent green over played region ──
    // Sits on top of the bars/baseline so everything beneath shows through
    // with a green tint. The area ahead of the playhead is untouched.
    if (progressX > 0) {
      ctx.globalAlpha = 1;
      ctx.fillStyle   = `rgba(${progressColorRGB}, 0.28)`;
      ctx.fillRect(0, 0, progressX, H);
    }

    // ── Playhead line ────────────────────────────────────────────────
    if (progress > 0 && progress < 1) {
      ctx.globalAlpha = 0.85;
      ctx.fillStyle   = progressColor;
      ctx.fillRect(Math.round(progressX) - 1, 0, 2, H);
    }

    ctx.globalAlpha = 1;
  }

  return { init, connect };

})();
(function () {
  function initTRVisualizer() {
    var canvas = document.getElementById('music-player-visualizer');

    if (!canvas) {
      return;
    }

    if (canvas.dataset.trVisualizerInitialized === 'true') {
      return;
    }

    if (typeof Visualizer === 'undefined') {
      console.warn('TR Radio: Visualizer is not loaded.');
      return;
    }

    canvas.dataset.trVisualizerInitialized = 'true';

    Visualizer.init(canvas, {
      getProgress: function () {
        if (!window.Player || typeof window.Player.progress !== 'function') {
          return 0;
        }

        return window.Player.progress();
      },

      onSeek: function (ratio) {
        if (!window.Player || typeof window.Player.seek !== 'function') {
          return;
        }

        window.Player.seek(ratio);
      }
    });

    console.log('TR Radio visualizer initialized without audio-node capture');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTRVisualizer);
  } else {
    initTRVisualizer();
  }

  document.addEventListener('turbo:load', initTRVisualizer);
})();